'use client'

import { X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Image, InputText, Navigation, Text } from '@vinted/web-ui'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/shared/api-client'
import { navigateToPage } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ContentLoader } from '@marketplace-web/shared/ui-helpers'
import { renderValidation } from '@marketplace-web/shared/react-hook-form'
import { useAsset } from '@marketplace-web/domain/assets'
import { TransactionModel } from '@marketplace-web/domain/conversation-messaging-data'

import { partialRefundOffersClickEvent } from '_libs/common/event-tracker/events'
import { formatAmount, formatCurrency, formatCurrencyAmount } from '_libs/utils/formatString'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { getRefundOfferCreationDetails } from 'data/api/get-refund-offer-creation-details'
import { sendRefundOffer } from 'data/api/send-refund-offer'
import { transformRefundOfferCreationDetailsDto } from 'data/transformers/refund-offer-creation-details'
import { ComplaintModel } from 'types/models/complaint'

import { RefundOfferFormField, RefundOfferFormModel } from '../types'

type Props = {
  isOpen: boolean
  url?: string
  complaint?: ComplaintModel | null
  transaction?: TransactionModel | null
  onClose: () => void
  onSubmitError: () => void
}

const EMPTY_ORDER_IMAGE = 'empty-hanger.svg'
const NOT_DIGITS_OR_WHITESPACE = /^[^\d\s]+/
const DEFAULT_MAX_PRICE_LENGTH = 7

const PartialRefundOfferDialog = ({
  isOpen,
  url,
  complaint,
  transaction,
  onClose,
  onSubmitError,
}: Props) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RefundOfferFormModel>()
  const { locale } = useIntl()
  const { track } = useTracking()
  const asset = useAsset('/assets/no-photo')
  const translate = useTranslate('partial_refund_offer_dialog')

  const refundOffer = watch(RefundOfferFormField.RefundOffer)

  const transactionOrder = transaction?.order
  const totalCombinedPrice = useMemo(
    () => complaint?.estimatedRelease?.totalPrice || transaction?.offer?.price,
    [complaint, transaction?.offer?.price],
  )
  const maxPriceLength =
    totalCombinedPrice?.amount?.split('.')[0]?.length || DEFAULT_MAX_PRICE_LENGTH
  const currencySign = totalCombinedPrice
    ? formatCurrencyAmount(
        { amount: '', currencyCode: totalCombinedPrice.currencyCode },
        locale,
      ).match(NOT_DIGITS_OR_WHITESPACE)?.[0] || ''
    : ''

  const [isFieldFocused, setIsFieldFocused] = useState(false)
  const [remainingEarnings, setRemainingEarnings] = useState(totalCombinedPrice?.amount)

  const {
    fetch: fetchOfferDetails,
    transformedData: refundOfferDetails,
    isLoading: isOfferDetailsLoading,
  } = useFetch(getRefundOfferCreationDetails, transformRefundOfferCreationDetailsDto)

  useEffect(() => {
    if (isOpen && complaint?.id) {
      fetchOfferDetails(complaint.id)
    }
  }, [isOpen, complaint?.id, fetchOfferDetails])

  const resetForm = () => {
    reset({ refund_offer: '' })
    setRemainingEarnings(totalCombinedPrice?.amount)
  }

  const handleDialogClose = () => {
    resetForm()
    onClose()
  }

  const stripCurrencyFormatting = (value: string): number =>
    parseFloat(value.replace(currencySign, '').replace(',', '.'))

  const trackRefundOfferClick = (refundOfferId: number) => {
    if (!complaint?.id) return

    const targetDetails = JSON.stringify({
      partial_refund_amount: stripCurrencyFormatting(refundOffer),
      refund_offer_id: refundOfferId?.toString(),
    })

    track(
      partialRefundOffersClickEvent({
        target: ClickableElement.OfferPartialRefund,
        transactionId: complaint.transactionId.toString(),
        complaintId: complaint.id.toString(),
        targetDetails,
      }),
    )
  }

  const handleFormSubmit = async () => {
    if (!complaint?.id || !totalCombinedPrice?.currencyCode) return

    const response = await sendRefundOffer({
      complaintId: Number(complaint.id),
      refundOffer: {
        amount: stripCurrencyFormatting(refundOffer).toString(),
        currencyCode: totalCombinedPrice.currencyCode,
      },
    })

    if ('errors' in response) {
      onSubmitError()

      return
    }

    trackRefundOfferClick(response.refund_offer.id)
    handleDialogClose()

    if (url) navigateToPage(url)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const offer = formatAmount(event.target.value, { maxLength: maxPriceLength }) || ''
    const earnings = Number(totalCombinedPrice?.amount) - (Number(offer) || 0)

    setValue(RefundOfferFormField.RefundOffer, offer)
    setRemainingEarnings(earnings.toString())
  }

  const handleInputBlur = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value || !totalCombinedPrice?.currencyCode) return

    const offer = formatCurrencyAmount(
      { amount: event.target.value, currencyCode: totalCombinedPrice.currencyCode },
      locale,
    )

    setIsFieldFocused(false)
    setValue(RefundOfferFormField.RefundOffer, offer)
  }

  const handleInputFocus = (event: ChangeEvent<HTMLInputElement>) => {
    const offer = formatAmount(event.target.value, { maxLength: maxPriceLength }) || ''

    setIsFieldFocused(true)
    setValue(RefundOfferFormField.RefundOffer, offer)
  }

  const getRemainingEarningsHint = () => {
    if (!totalCombinedPrice || !remainingEarnings) return null

    return (
      <Text
        as="h4"
        type={Text.Type.Caption}
        text={translate('hint', {
          amount: formatCurrencyAmount(
            { amount: remainingEarnings, currencyCode: totalCombinedPrice.currencyCode },
            locale,
          ),
        })}
      />
    )
  }

  const getInputValidation = () => {
    const errorMessage = errors[RefundOfferFormField.RefundOffer]?.message

    return errorMessage ? renderValidation(errorMessage) : getRemainingEarningsHint()
  }

  const renderCell = (thumbnail, title, priceAmount, currencyCode) => (
    <Cell
      prefix={
        <Image
          src={thumbnail || asset(EMPTY_ORDER_IMAGE)}
          styling={Image.Styling.Rounded}
          size={Image.Size.Large}
          scaling={Image.Scaling.Cover}
        />
      }
      title={title}
      body={formatCurrency(priceAmount, { locale, currency: currencyCode })}
      divider
    />
  )

  const renderOrderSummary = () => {
    const complaintItem = complaint?.complaintOrderItems?.[0]
    if (complaintItem && complaint.estimatedRelease?.totalPrice) {
      return renderCell(
        complaintItem.item.thumbnail,
        complaintItem.item.title,
        complaint.estimatedRelease.totalPrice.amount,
        complaint.estimatedRelease.totalPrice.currencyCode,
      )
    }

    // TODO: Remove when there is no more old complaint flow
    const transactionItem = transactionOrder?.items?.[0]
    if (transactionItem && transactionOrder?.price) {
      return renderCell(
        transactionItem.thumbnailUrl,
        transactionItem.title,
        transactionOrder.price.amount,
        transactionOrder.price.currencyCode,
      )
    }

    return null
  }

  const renderRefundOfferInput = () => {
    if (!refundOfferDetails) return null

    return (
      <InputText
        title={translate('refund_amount_label')}
        {...register(RefundOfferFormField.RefundOffer, {
          validate: {
            min: value =>
              stripCurrencyFormatting(value) >= parseFloat(refundOfferDetails.minAmount.amount) ||
              translate('refund_offer.errors.min', {
                amount: formatCurrencyAmount(refundOfferDetails.minAmount, locale),
              }),
            max: value =>
              stripCurrencyFormatting(value) <= parseFloat(refundOfferDetails.maxAmount.amount) ||
              translate('refund_offer.errors.max', {
                amount: formatCurrencyAmount(refundOfferDetails.maxAmount, locale),
              }),
          },
          required: { value: true, message: translate('refund_offer.errors.required') },
        })}
        placeholder={formatCurrencyAmount(
          { amount: '', currencyCode: refundOfferDetails.maxAmount.currencyCode },
          locale,
        )}
        validation={getInputValidation()}
        onChange={event => {
          handleInputChange(event)
          register(RefundOfferFormField.RefundOffer).onChange(event)
        }}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
      />
    )
  }

  const renderActions = () => {
    if (!totalCombinedPrice?.currencyCode) return null

    return (
      <Cell
        body={
          <Button
            type={Button.Type.Submit}
            text={
              refundOffer
                ? translate('actions.submit', {
                    amount: isFieldFocused
                      ? formatCurrencyAmount(
                          { amount: refundOffer, currencyCode: totalCombinedPrice.currencyCode },
                          locale,
                        )
                      : refundOffer,
                  })
                : translate('actions.submit_empty')
            }
            styling={Button.Styling.Filled}
            disabled={isSubmitting}
            isLoading={isSubmitting}
          />
        }
      />
    )
  }

  const renderNavigation = () => {
    return (
      <>
        <Navigation
          body={translate('title')}
          right={
            <Button
              inline
              iconName={X24}
              styling={Button.Styling.Flat}
              onClick={handleDialogClose}
              aria={{ 'aria-label': translate('a11y.close') }}
            />
          }
        />
        <Divider />
      </>
    )
  }

  const renderContent = () =>
    isOfferDetailsLoading ? (
      <ContentLoader />
    ) : (
      <>
        {renderOrderSummary()}
        {renderRefundOfferInput()}
        {renderActions()}
      </>
    )

  if (!complaint && !transactionOrder) return null

  return (
    <Dialog show={isOpen}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {renderNavigation()}
        {renderContent()}
      </form>
    </Dialog>
  )
}

export default PartialRefundOfferDialog
